import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from 'src/store';
import { Badge } from 'src/shared/components/ui/badge';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import { resetPortfolioFilters, updatePortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';

const getFilterTestIdSegment = (value: string) => value.replace(/\s+/g, '-');

const PortfolioActiveFilters = () => {
    const dispatch = useAppDispatch();
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);
    const [isExpanded, setIsExpanded] = useState(true);
    const selectedBadgesCount = selectedFilters.reduce((total, filter) => total + filter.values.length, 0);

    if (!selectedFilters.length) {
        return null;
    }

    const removeFilterValue = (filterName: string, filterValue: string) => {
        const nextFilters = selectedFilters
            .map(filter => {
                if (filter.name !== filterName) {
                    return filter;
                }

                return {
                    ...filter,
                    values: filter.values.filter(value => value !== filterValue),
                };
            })
            .filter(filter => filter.values.length > 0);

        dispatch(updatePortfolioFilters(nextFilters));
    };

    const clearAllFilters = () => {
        dispatch(resetPortfolioFilters());
    };

    return (
        <div className='flex flex-col gap-2' data-testid='portfolio-active-filters-section'>
            <div>
                <button
                    type='button'
                    onClick={() => setIsExpanded(prev => !prev)}
                    className='text-xl text-black inline-flex items-center gap-2'
                    aria-expanded={isExpanded}
                    aria-controls='portfolio-active-filters-badges'
                    data-testid='portfolio-active-filters-heading'
                >
                    <span>Active Filters</span>
                    {isExpanded ? <ChevronUp className='h-5 w-5' /> : <ChevronDown className='h-5 w-5' />}
                </button>
            </div>
            {isExpanded ? (
                <div className='flex flex-wrap gap-2' data-testid='portfolio-active-filters-badges' id='portfolio-active-filters-badges'>
                    {selectedFilters.flatMap(filter =>
                        filter.values.map(value => {
                            const testIdSegment = getFilterTestIdSegment(`${filter.name}-${value}`);
                            return (
                                <Badge
                                    key={`${filter.name}-${value}`}
                                    variant='outline'
                                    className='flex items-center gap-1 border-grey-20 bg-white text-black text-xs font-normal px-3 py-1.5'
                                    data-testid={`portfolio-active-filter-badge-${testIdSegment}`}
                                >
                                    <span>{`${filter.name}: ${value}`}</span>
                                    <button
                                        type='button'
                                        aria-label={`Remove ${filter.name}: ${value} filter`}
                                        onClick={() => removeFilterValue(filter.name, value)}
                                        className='inline-flex items-center justify-center text-grey-80 hover:text-black'
                                        data-testid={`portfolio-active-filter-remove-${testIdSegment}`}
                                    >
                                        <X className='h-3.5 w-3.5' />
                                    </button>
                                </Badge>
                            );
                        })
                    )}
                    {selectedBadgesCount > 1 ? (
                        <button
                            type='button'
                            onClick={clearAllFilters}
                            className='inline-flex items-center text-xs underline text-grey-80 hover:text-black'
                            data-testid='portfolio-active-filters-clear-all'
                        >
                            Clear
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default PortfolioActiveFilters;
